import React from 'react';
import { Toaster } from 'sonner';
import { createGlobalStyle, useTheme } from 'styled-components';

import VipIcon from '../../Icons/VipIcons';

// Toasts slide and fade with CSS transitions; people who ask for less motion get none
const ReducedMotion = createGlobalStyle`
  @media (prefers-reduced-motion: reduce) {
    [data-sonner-toaster],
    [data-sonner-toast] {
      transition: none !important;
      animation: none !important;
    }
  }
`;

const ICON_SIZE = 20;

// Neutral toasts: only the icon carries the status colour.
const ToastStyles = createGlobalStyle`
  [data-sonner-toast] {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06) !important;
    padding: 12px 18px !important;
  }

  [data-sonner-toast] [data-icon] {
    margin-right: 2px;
  }

  [data-sonner-toast][data-type='success'] [data-icon] {
    color: ${({ $colors }) => $colors.success};
  }

  [data-sonner-toast][data-type='error'] [data-icon] {
    color: ${({ $colors }) => $colors.error};
  }

  [data-sonner-toast][data-type='warning'] [data-icon] {
    color: ${({ $colors }) => $colors.warning};
  }

  [data-sonner-toast][data-type='info'] [data-icon] {
    color: ${({ $colors }) => $colors.textSecondary || $colors.text};
  }
`;

// The one place toasts are drawn. Sonner reads its colours and shape from these CSS variables.
const ToastProvider = ({ children }) => {
  const theme = useTheme();

  const colors = theme?.colors || {
    background: '#ffffff',
    text: '#333333',
    border: '#e5e5e5',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    textSecondary: '#6e7a8a',
    cardBg: '#ffffff',
  };

  const variables = {
    '--width': 'min(400px, calc(100vw - 24px))',
    '--border-radius': '28px',
    '--normal-bg': colors.cardBg || colors.background,
    '--normal-text': colors.text,
    '--normal-border': colors.border,
  };

  return (
    <>
      {children}
      <ReducedMotion />
      <ToastStyles $colors={colors} />
      <Toaster
        position="top-center"
        visibleToasts={3}
        duration={4000}
        offset={16}
        mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        containerAriaLabel="Notifications"
        style={variables}
        toastOptions={{
          style: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
          },
        }}
        icons={{
          success: <VipIcon name="CheckmarkCircle" size={ICON_SIZE} />,
          error: <VipIcon name="CloseXCircle" size={ICON_SIZE} />,
          info: <VipIcon name="InfoIcon" size={ICON_SIZE} />,
          warning: <VipIcon name="AlertIcon" size={ICON_SIZE} />,
        }}
      />
    </>
  );
};

export default ToastProvider;
